import { html, unsafeCSS, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { ApiService } from '@trp-dms-digital-shared/ds-ui-library-api';

import type { SummaryComponentObject } from './summary.model';

import { SummaryFragments } from '../../common/fragments/summary.fragments';
import { PopApiBaseComponent } from '../../base/pop-api-base-component';
import { Constants } from './summary.constants.js';
import { dateShort } from '../../common/utils/formatDate';
import { isKeyFactWithRank } from '../../shared/key-fact/key-fact.utils';
import style from './summary.scss?inline';
import { Literature } from '../literature/literature.model';
import {
  isNullish,
  isEmptyObject,
  isEmptyArray,
} from '../../common/utils/nullish';

/**
 * @tag pop-summary-component
 * @attribute {SummaryComponentObject} [data-component-object] Summary model
 */
@customElement(Constants.TAG_NAME)
export class PopSummaryComponent extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  static readonly nestedComponents = Constants.NESTED_COMPONENTS;

  /**
   * @internal
   */
  fragment = SummaryFragments.summaryFragment;

  /**
   * The component object that contains the pre-fetched data to be rendered.
   */
  componentObject?: SummaryComponentObject;

  constructor() {
    super(new ApiService());
  }

  protected processResponse(_response): void {
    this.componentObject = _response.summaryExhibit;
  }

  renderProductName() {
    if (this.componentObject?.fundName) {
      return html`<slot name="product-name"></slot>`;
    }

    return html``;
  }

  renderProductNameUnavailable() {
    return html`<pop-no-data-available-component
      .message="${Constants.PRODUCT_NAME_UNAVAILABLE}"
    ></pop-no-data-available-component>`;
  }

  renderSubscribeButton() {
    if (
      this.componentObject?.productCode &&
      this.componentObject?.isLiteratureSubscribable
    ) {
      return html`
        <div class="b-grid-item--2-col">
          <slot name="subscription-button"></slot>
        </div>
      `;
    }

    return nothing;
  }

  handleSubscribeButtonClick(event: Event) {
    console.info('Subscribe button clicked - e:', event);
  }

  renderDownloadIcon() {
    return html`<beacon-icon slot="suffix" name="download"></beacon-icon>`;
  }

  handleFactSheetClick(literature: Literature) {
    window.open(literature.url, Constants.BLANK);
  }

  areLiteraturesAvailable(literatures: Literature[] | undefined) {
    return !(
      isNullish(literatures) ||
      isEmptyArray(literatures) ||
      isEmptyObject(literatures)
    );
  }

  renderLiterature() {
    const literatures = this.componentObject?.literatures;

    if (this.areLiteraturesAvailable(literatures)) {
      const factSheet = literatures?.find(
        (literature) =>
          literature.name.localeCompare(Constants.FACT_SHEET, undefined, {
            sensitivity: 'base',
          }) === 0,
      );

      if (factSheet) {
        return html`
          <beacon-button
            @beacon-event-click=${() => {
              this.handleFactSheetClick(factSheet);
            }}
            icon-align="right"
            variant="tertiary"
            scale="jumbo"
          >
            ${factSheet.name} ${this.renderDownloadIcon()}
          </beacon-button>
        `;
      }
    }

    return nothing;
  }

  renderAsOfDate() {
    if (this.componentObject?.asOfDate) {
      // Letting this next line wrap onto multiple lines breaks the unit test for this.
      // prettier-ignore
      const text = `Data is based on the representative portfolio as of ${dateShort(this.componentObject?.asOfDate)} unless otherwise noted`;

      return html` <div
        class="b-typography-sans-serif--caption--200 b-font-weight-light"
      >
        <span data-test-id="summary-as-of-date">${text}</span>
      </div>`;
    }

    return html``;
  }

  renderDataGrid() {
    if (this.componentObject?.keyFacts) {
      const filteredKeyFacts = this.componentObject.keyFacts
        .filter((keyFact) => keyFact.value)
        .filter(isKeyFactWithRank)
        .sort((a, b) => parseInt(a.rank) - parseInt(b.rank))
        .slice(0, 6);

      return html`
        ${filteredKeyFacts.map(
          (keyFact) => html`
            <div class="summary-grid-block">
              <pop-key-fact-component
                data-variant="summary-key-facts-block"
                data-title=${keyFact.title}
                data-content=${keyFact.value}
                data-sub-content=${keyFact.subContent}
                data-subtitle=${keyFact.subtitle}
                data-tooltip=${keyFact.tooltip}
                data-value-format=${keyFact.valueFormat}
                data-as-of-date=${keyFact.asOfDate &&
                this.componentObject?.asOfDate &&
                dateShort(keyFact.asOfDate) ===
                  dateShort(this.componentObject?.asOfDate)
                  ? ''
                  : keyFact.asOfDate}
              >
              </pop-key-fact-component>
            </div>
          `,
        )}
      `;
    }

    return html``;
  }

  renderSummarySearch() {
    return html` <pop-summary-search-component
      data-summary-search-placeholder="${Constants.SUMMARY_SEARCH_PLACEHOLDER}"
    >
    </pop-summary-search-component>`;
  }

  renderButtonSection() {
    const subscribeContent = this.renderSubscribeButton();
    const literatureContent = this.renderLiterature();

    if (subscribeContent !== nothing || literatureContent !== nothing) {
      return html`
        <div class="summary-button-container">
          ${subscribeContent} ${literatureContent}
        </div>
      `;
    }

    return ``;
  }

  render() {
    return this.renderComponent(html`
      <section class="content">
        <div class="summary-details-container b-container">
          <div class="summary-search-container">
            ${this.renderSummarySearch()}
          </div>
          ${this.componentObject?.fundName
            ? html`<div class="product-name">${this.renderProductName()}</div>
                ${this.renderButtonSection()}`
            : this.renderProductNameUnavailable()}
          <div class="b-padding--cell">${this.renderAsOfDate()}</div>
        </div>

        <div class="summary-key-facts b-container">
          <div class="data-grid-container">${this.renderDataGrid()}</div>
        </div>
      </section>
    `);
  }
}
